import type { Dispatch, SetStateAction } from "react";
import type { RtoTermDefinition } from "@/lib/rto/rtoTermSettings";
import {
  cardHoverSurfaceClass,
  cardMutedTextClass,
  cardSurfaceClass,
  compactInputClass,
  insetSurfaceCardClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";

export function RtoSettingsTermRow(props: {
  term: RtoTermDefinition;
  idx: number;
  rowCount: number;
  rtoStates: string[];
  openStateOverridesByTerm: Record<string, boolean>;
  setOpenStateOverridesByTerm: Dispatch<SetStateAction<Record<string, boolean>>>;
  openRtoActionsFor: string | null;
  setOpenRtoActionsFor: Dispatch<SetStateAction<string | null>>;
  setRtoTerms: Dispatch<SetStateAction<RtoTermDefinition[]>>;
}) {
  const {
    term,
    idx,
    rowCount,
    rtoStates,
    openStateOverridesByTerm,
    setOpenStateOverridesByTerm,
    openRtoActionsFor,
    setOpenRtoActionsFor,
    setRtoTerms,
  } = props;

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-8 gap-2 p-3 rounded-lg border ${secondarySurfaceClass}`}>
      <input
        className={`text-xs ${compactInputClass}`}
        value={term.name}
        onChange={(e) =>
          setRtoTerms((prev) => prev.map((t, i) => (i === idx ? { ...t, name: e.target.value } : t)))
        }
        placeholder="Name"
      />
      <input
        className={`text-xs ${compactInputClass}`}
        type="number"
        value={term.term_months}
        onChange={(e) =>
          setRtoTerms((prev) => prev.map((t, i) => (i === idx ? { ...t, term_months: Number(e.target.value) || 1 } : t)))
        }
        placeholder="Months"
      />
      <input
        className={`text-xs ${compactInputClass}`}
        type="number"
        step="0.01"
        value={term.divisor}
        onChange={(e) =>
          setRtoTerms((prev) => prev.map((t, i) => (i === idx ? { ...t, divisor: Number(e.target.value) || 0.01 } : t)))
        }
        placeholder="Divisor"
      />
      <select
        className={`text-xs pr-7 ${compactInputClass}`}
        value={term.required_down_type}
        onChange={(e) =>
          setRtoTerms((prev) =>
            prev.map((t, i) =>
              i === idx ? { ...t, required_down_type: e.target.value === "percent" ? "percent" : "payments" } : t
            )
          )
        }
      >
        <option value="payments">Payments</option>
        <option value="percent">Percentage</option>
      </select>
      <input
        className={`text-xs ${compactInputClass}`}
        type="number"
        step="0.01"
        value={term.required_down_value}
        onChange={(e) =>
          setRtoTerms((prev) =>
            prev.map((t, i) => (i === idx ? { ...t, required_down_value: Number(e.target.value) || 0 } : t))
          )
        }
        placeholder={term.required_down_type === "percent" ? "0.10" : "2"}
      />
      <label className="inline-flex items-center justify-center text-xs">
        <input
          type="checkbox"
          checked={term.balloon_required}
          onChange={(e) =>
            setRtoTerms((prev) => prev.map((t, i) => (i === idx ? { ...t, balloon_required: e.target.checked } : t)))
          }
        />
      </label>
      <input
        className={`text-xs ${compactInputClass}`}
        type="number"
        value={term.balloon_payments}
        disabled={!term.balloon_required}
        onChange={(e) =>
          setRtoTerms((prev) =>
            prev.map((t, i) => (i === idx ? { ...t, balloon_payments: Number(e.target.value) || 0 } : t))
          )
        }
        placeholder="Balloon pmts"
      />
      {rtoStates.length > 0 ? (
        <div className="sm:col-span-8">
          <button
            type="button"
            className="text-[11px] text-indigo-700 underline underline-offset-2 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            onClick={() =>
              setOpenStateOverridesByTerm((prev) => ({
                ...prev,
                [term.id]: !prev[term.id],
              }))
            }
          >
            {openStateOverridesByTerm[term.id] ? "Hide payoff % by state overrides" : "Show payoff % by state overrides"}
          </button>
          {openStateOverridesByTerm[term.id] ? (
            <div className={`mt-2 rounded p-2 ${insetSurfaceCardClass}`}>
              <p className={`text-[11px] font-medium mb-1 ${cardMutedTextClass}`}>Payoff % by state (override)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {rtoStates.map((st) => (
                  <label key={`${term.id}-${st}`} className={`text-[11px] ${cardMutedTextClass}`}>
                    {st}
                    <input
                      className={`mt-1 w-full text-xs ${compactInputClass}`}
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={term.early_purchase_percent_by_state?.[st] ?? term.early_purchase_percent}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setRtoTerms((prev) =>
                          prev.map((t, i) =>
                            i === idx
                              ? {
                                  ...t,
                                  early_purchase_percent_by_state: {
                                    ...(t.early_purchase_percent_by_state ?? {}),
                                    [st]: Number.isFinite(next) && next > 0 ? next : t.early_purchase_percent,
                                  },
                                }
                              : t
                          )
                        );
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="relative">
        <button
          type="button"
          className={`text-xs rounded px-2 py-1.5 border ${cardHoverSurfaceClass}`}
          onClick={() => setOpenRtoActionsFor((prev) => (prev === term.id ? null : term.id))}
        >
          Actions
        </button>
        {openRtoActionsFor === term.id ? (
          <div className={`absolute right-0 z-10 mt-1 w-32 rounded p-1 shadow-lg text-xs ${cardSurfaceClass}`}>
            <button
              type="button"
              className={`block w-full rounded px-2 py-1 text-left disabled:opacity-40 ${cardHoverSurfaceClass}`}
              disabled={idx === 0}
              onClick={() => {
                setRtoTerms((prev) => {
                  const next = [...prev];
                  const [item] = next.splice(idx, 1);
                  next.splice(idx - 1, 0, item);
                  return next;
                });
                setOpenRtoActionsFor(null);
              }}
            >
              Move up
            </button>
            <button
              type="button"
              className={`block w-full rounded px-2 py-1 text-left disabled:opacity-40 ${cardHoverSurfaceClass}`}
              disabled={idx === rowCount - 1}
              onClick={() => {
                setRtoTerms((prev) => {
                  const next = [...prev];
                  const [item] = next.splice(idx, 1);
                  next.splice(idx + 1, 0, item);
                  return next;
                });
                setOpenRtoActionsFor(null);
              }}
            >
              Move down
            </button>
            <button
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => {
                setRtoTerms((prev) => prev.filter((_, i) => i !== idx));
                setOpenRtoActionsFor(null);
              }}
            >
              Remove
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
